import type { FaceDetector, ObjectDetector } from "@mediapipe/tasks-vision";
import type { DetectedFace } from "./auto-reframe-engine";
import {
  detectionsToSubjects,
  resolveVisionAssetUrls,
  runDetectionChain,
  type SubjectDetectionResult,
  type SubjectDetectionStage,
  type VisionAssetUrls,
} from "./reframe-subject-detection";
import {
  centerFramedFallbackSubject,
  detectSkinToneSubjects,
  type ImageDataLike,
} from "./reframe-skin-tone-detector";

type VisionModule = typeof import("@mediapipe/tasks-vision");
// WasmFileset is declared but not exported by the package, so name it through
// the signature that consumes it.
type VisionFileset = Parameters<
  VisionModule["FaceDetector"]["createFromOptions"]
>[0];

const DEFAULT_MIN_FACE_CONFIDENCE = 0.5;
const DEFAULT_MIN_OBJECT_CONFIDENCE = 0.4;

// EfficientDet-Lite0 predicts COCO classes. When a person is on screen they are
// the subject, even when a wall or a table behind them covers more pixels.
const PERSON_CATEGORIES = ["person"] as const;

export interface SubjectDetectorOptions {
  /** Overrides where the wasm runtime and .tflite models are fetched from. */
  assetBaseUrl?: string;
  minFaceConfidence?: number;
  minObjectConfidence?: number;
}

export interface SubjectDetectionRequest {
  frame: ImageBitmap;
  timestampMs: number;
  /** Only invoked once both model-backed stages have come up empty. */
  readImageData: () => ImageDataLike | null;
}

/**
 * Face first, objects when no face is found, skin tone only when neither model
 * could be loaded. Every stage failure is swallowed: reframing degrades to a
 * worse subject signal rather than failing the clip.
 */
export class ReframeSubjectDetector {
  private readonly assetUrls: VisionAssetUrls;
  private readonly minFaceConfidence: number;
  private readonly minObjectConfidence: number;
  private faceDetector: FaceDetector | null = null;
  private objectDetector: ObjectDetector | null = null;
  private visionPromise: Promise<VisionModule | null> | null = null;
  private filesetPromise: Promise<VisionFileset | null> | null = null;
  private facePromise: Promise<FaceDetector | null> | null = null;
  private objectPromise: Promise<ObjectDetector | null> | null = null;
  private lastModelTimestamp = 0;

  constructor(options: SubjectDetectorOptions = {}) {
    this.assetUrls = resolveVisionAssetUrls(options.assetBaseUrl);
    this.minFaceConfidence =
      options.minFaceConfidence ?? DEFAULT_MIN_FACE_CONFIDENCE;
    this.minObjectConfidence =
      options.minObjectConfidence ?? DEFAULT_MIN_OBJECT_CONFIDENCE;
  }

  /** Resolves true when at least the face model is ready to run. */
  async prepare(): Promise<boolean> {
    return (await this.ensureFaceDetector()) !== null;
  }

  isModelBacked(): boolean {
    return this.faceDetector !== null || this.objectDetector !== null;
  }

  getAssetUrls(): VisionAssetUrls {
    return this.assetUrls;
  }

  async detect(
    request: SubjectDetectionRequest,
  ): Promise<SubjectDetectionResult> {
    const stages: SubjectDetectionStage[] = [
      {
        source: "face",
        run: () => this.runFaceDetector(request),
      },
      {
        source: "object",
        run: () => this.runObjectDetector(request),
      },
      {
        source: "skin-tone",
        run: () => this.runSkinToneDetector(request),
      },
      // Its own stage, because it is not a detection. Folding it into the
      // skin-tone stage made that stage incapable of returning nothing, which
      // in turn made "no subject was found" unreachable and let the UI report
      // an invented box as a heuristic that had found something.
      {
        source: "centre-fallback",
        run: () => this.runCentreFallback(request),
      },
    ];

    return runDetectionChain(stages);
  }

  close(): void {
    this.faceDetector?.close();
    this.objectDetector?.close();
    this.faceDetector = null;
    this.objectDetector = null;
    this.visionPromise = null;
    this.filesetPromise = null;
    this.facePromise = null;
    this.objectPromise = null;
    this.lastModelTimestamp = 0;
  }

  private async runFaceDetector(
    request: SubjectDetectionRequest,
  ): Promise<DetectedFace[]> {
    const detector = await this.ensureFaceDetector();
    if (!detector) return [];

    const { detections } = detector.detectForVideo(
      request.frame,
      this.nextModelTimestamp(request.timestampMs),
    );

    return detectionsToSubjects(
      detections,
      request.frame.width,
      request.frame.height,
      { minConfidence: this.minFaceConfidence },
    );
  }

  private async runObjectDetector(
    request: SubjectDetectionRequest,
  ): Promise<DetectedFace[]> {
    const detector = await this.ensureObjectDetector();
    if (!detector) return [];

    const { detections } = detector.detectForVideo(
      request.frame,
      this.nextModelTimestamp(request.timestampMs),
    );
    const width = request.frame.width;
    const height = request.frame.height;

    const people = detectionsToSubjects(detections, width, height, {
      minConfidence: this.minObjectConfidence,
      allowedCategories: PERSON_CATEGORIES,
    });
    if (people.length > 0) return people;

    return detectionsToSubjects(detections, width, height, {
      minConfidence: this.minObjectConfidence,
    });
  }

  private runSkinToneDetector(
    request: SubjectDetectionRequest,
  ): DetectedFace[] {
    const image = request.readImageData();
    if (!image) return [];

    return detectSkinToneSubjects(image);
  }

  /** Frames the middle when nothing was detected, reported as exactly that. */
  private runCentreFallback(
    request: SubjectDetectionRequest,
  ): DetectedFace[] {
    const image = request.readImageData();
    if (!image) return [];

    return [centerFramedFallbackSubject(image.width, image.height)];
  }

  /**
   * MediaPipe rejects a video timestamp that does not advance, and sampled
   * frames can share a millisecond at high frame rates. Both tasks draw from
   * this one counter so neither ever sees the clock go backwards.
   */
  private nextModelTimestamp(timestampMs: number): number {
    const candidate = Number.isFinite(timestampMs) ? Math.round(timestampMs) : 0;
    this.lastModelTimestamp = Math.max(candidate, this.lastModelTimestamp + 1);
    return this.lastModelTimestamp;
  }

  private async loadVision(): Promise<VisionModule | null> {
    if (!this.visionPromise) {
      this.visionPromise = import("@mediapipe/tasks-vision").catch(() => null);
    }
    return this.visionPromise;
  }

  private async ensureFileset(): Promise<VisionFileset | null> {
    if (!this.filesetPromise) {
      this.filesetPromise = this.loadVision().then((vision) => {
        if (!vision) return null;
        return vision.FilesetResolver.forVisionTasks(
          this.assetUrls.wasmBase,
        ).catch(() => null);
      });
    }
    return this.filesetPromise;
  }

  private async ensureFaceDetector(): Promise<FaceDetector | null> {
    if (!this.facePromise) {
      this.facePromise = this.createFaceDetector();
    }
    return this.facePromise;
  }

  private async createFaceDetector(): Promise<FaceDetector | null> {
    const [vision, fileset] = await Promise.all([
      this.loadVision(),
      this.ensureFileset(),
    ]);
    if (!vision || !fileset) return null;

    const create = (delegate: "GPU" | "CPU") =>
      vision.FaceDetector.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: this.assetUrls.faceModel,
          delegate,
        },
        runningMode: "VIDEO",
        minDetectionConfidence: this.minFaceConfidence,
      });

    try {
      this.faceDetector = await create("GPU").catch(() => create("CPU"));
    } catch {
      return null;
    }
    return this.faceDetector;
  }

  private async ensureObjectDetector(): Promise<ObjectDetector | null> {
    if (!this.objectPromise) {
      this.objectPromise = this.createObjectDetector();
    }
    return this.objectPromise;
  }

  private async createObjectDetector(): Promise<ObjectDetector | null> {
    const [vision, fileset] = await Promise.all([
      this.loadVision(),
      this.ensureFileset(),
    ]);
    if (!vision || !fileset) return null;

    const create = (delegate: "GPU" | "CPU") =>
      vision.ObjectDetector.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: this.assetUrls.objectModel,
          delegate,
        },
        runningMode: "VIDEO",
        scoreThreshold: this.minObjectConfidence,
      });

    try {
      this.objectDetector = await create("GPU").catch(() => create("CPU"));
    } catch {
      return null;
    }
    return this.objectDetector;
  }
}
